import { useEffect, useState } from "react";
import { api, clearToken } from "../lib/api.js";
import { greeting } from "../lib/format.js";

import Sidebar from "../components/Sidebar.jsx";
import Icon from "../components/Icon.jsx";
import FulminioDecision from "../components/FulminioDecision.jsx";
import PitchOutline from "../components/PitchOutline.jsx";
import OpportunityCard from "../components/OpportunityCard.jsx";
import ProspectTable from "../components/ProspectTable.jsx";
import MetricCards from "../components/MetricCards.jsx";
import Briefing from "../components/Briefing.jsx";
import SponsorCampaigns from "./SponsorCampaigns.jsx";
import Dashboard from "./Dashboard.jsx";

export default function CommandCenter({ token }) {
  const [view, setView] = useState("command");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [radarRunning, setRadarRunning] = useState(false);
  const [radarResult, setRadarResult] = useState(null);

  const [drafting, setDrafting] = useState(null);
  const [draft, setDraft] = useState(null);
  const [pitching, setPitching] = useState(null);
  const [pitch, setPitch] = useState(null);

  useEffect(() => {
    load();
  }, [token]);

  async function load() {
    setLoading(true);
    try {
      setData(await api.commandCenter(token));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function runRadar() {
    setRadarRunning(true);
    setRadarResult(null);
    try {
      const res = await api.runRadar(token);
      setRadarResult(res);
      if (res.success && res.processed > 0) load();
    } catch {
      setRadarResult({ error: "Radar failed" });
    }
    setRadarRunning(false);
  }

  async function onDraft(o) {
    setDrafting(o.id);
    setDraft(null);
    setPitch(null);
    try {
      const res = await api.draftEmail(token, o.id, o.contact_id || o.id);
      setDraft(res.success ? { ...res.email, company: o.company } : { error: res.error });
    } catch {
      setDraft({ error: "Connection error" });
    }
    setDrafting(null);
  }

  async function onPitch(o, regenerate = false) {
    setPitching(o.id);
    setPitch(null);
    setDraft(null);
    try {
      const res = await api.buildPitch(token, o.id, regenerate);
      setPitch(
        res.success ? { ...res.pitch, company: o.company, cached: res.cached } : { error: res.error }
      );
    } catch {
      setPitch({ error: "Connection error" });
    }
    setPitching(null);
  }

  const opportunities = data?.opportunities || [];
  const signals = data?.signals || [];
  const actions = data?.actions || [];
  const learned = data?.learned || [];
  const best = opportunities[0];
  const runnersUp = opportunities.slice(1, 3);

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar
        view={view}
        onChange={setView}
        plan={data?.tenant?.plan}
        prospectCount={opportunities.length}
        signalCount={signals.length}
        onSignOut={() => {
          clearToken();
          location.reload();
        }}
      />

      {view === "dashboard" ? (
        <Dashboard />
      ) : view === "sponsor" ? (
        <div style={{ flex: 1, overflow: "auto" }}>
          <SponsorCampaigns />
        </div>
      ) : (
      <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
        <header
          style={{
            padding: "18px 28px",
            borderBottom: "1px solid var(--brd)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h1 style={{ fontFamily: "var(--d)", fontSize: 24, fontWeight: 700 }}>
              {greeting()}, {data?.tenant?.name || "there"}
            </h1>
            <div style={{ fontSize: 13, color: "var(--t2)", marginTop: 2 }}>
              Fulminio active · scanning markets
            </div>
          </div>
          <button
            onClick={runRadar}
            disabled={radarRunning}
            style={{
              padding: "9px 20px",
              borderRadius: 8,
              border: "none",
              background: radarRunning ? "var(--s2)" : "var(--volt)",
              color: radarRunning ? "var(--t3)" : "#0A0A0F",
              fontSize: 13,
              fontWeight: 700,
              cursor: radarRunning ? "wait" : "pointer",
              fontFamily: "var(--d)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Icon name="bolt" size={14} color={radarRunning ? "var(--t3)" : "#0A0A0F"} fill />
            {radarRunning ? "Scanning…" : "Run Radar"}
          </button>
        </header>

        {loading ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--t3)",
            }}
          >
            Loading…
          </div>
        ) : (
          <div style={{ flex: 1, overflow: "auto", display: "flex" }}>
            <div style={{ flex: 1, padding: "22px 28px", minWidth: 0, overflow: "auto" }}>
              {radarResult && (
                <div
                  className="a"
                  style={{
                    padding: "10px 14px",
                    borderRadius: 8,
                    background: radarResult.success
                      ? "rgba(34,197,94,0.05)"
                      : "rgba(239,68,68,0.05)",
                    border: `1px solid ${
                      radarResult.success ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)"
                    }`,
                    marginBottom: 16,
                    fontSize: 13,
                    color: radarResult.success ? "var(--green)" : "var(--red)",
                  }}
                >
                  {radarResult.success
                    ? `Found ${radarResult.companies_found}, added ${radarResult.processed} to pipeline.`
                    : radarResult.error}
                </div>
              )}

              <FulminioDecision
                best={best}
                signals={signals}
                onDraft={onDraft}
                onPitch={onPitch}
                drafting={drafting}
                pitching={pitching}
                draft={draft}
                onDismissDraft={() => setDraft(null)}
              />

              <PitchOutline pitch={pitch} onClose={() => setPitch(null)} />

              {runnersUp.length > 0 && (
                <div className="a a1" style={{ marginBottom: 22 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--t2)", marginBottom: 10 }}>
                    Next best opportunities
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {runnersUp.map((o) => (
                      <OpportunityCard
                        key={o.id}
                        opportunity={o}
                        signals={signals}
                        drafting={drafting}
                        pitching={pitching}
                        onDraft={onDraft}
                        onPitch={onPitch}
                      />
                    ))}
                  </div>
                </div>
              )}

              <ProspectTable
                opportunities={opportunities}
                drafting={drafting}
                onDraft={onDraft}
              />

              <MetricCards actions={actions} signals={signals} learned={learned} />
            </div>

            <Briefing signals={signals} actions={actions} learned={learned} />
          </div>
        )}
      </div>
      )}
    </div>
  );
}


